/**
 * Cloud Icon Renderer - AWS Resource node icon
 *
 * Renders a simple cloud icon used for representing AWS resources
 * in the graph visualization.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  calculateIconPosition,
  drawCircleBackground,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';

/**
 * Create cloud icon data URL
 *
 * Generates a cloud icon with circular background for use as
 * a node image in the graph visualization.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @param isHighlighted - Whether the icon should appear highlighted
 * @returns Data URL string of the rendered icon
 */
export const createCloudIconDataURL = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean = false
): string => {
  const config = getIconConfig('cloud');
  const colors = getThemeColors('cloud', currentTheme, isHighlighted);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(ctx, centerX, centerY, size, config.scale, colors);

  // Calculate icon position
  const { x, y, scale } = calculateIconPosition(centerX, centerY, size, config);

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.fillStyle = colors.icon;

  // Draw cloud shape
  ctx.beginPath();

  // Start from bottom left
  ctx.moveTo(6, 20);

  // Left side bottom curve
  ctx.bezierCurveTo(6, 20, 4, 18, 4, 16);

  // Left side
  ctx.bezierCurveTo(4, 13, 6, 11, 8, 10.5);

  // Top left bump
  ctx.bezierCurveTo(8, 8, 10, 6, 12.5, 6);

  // Top middle bump
  ctx.bezierCurveTo(14, 4, 16, 4, 18, 5);

  // Top right bump
  ctx.bezierCurveTo(20, 5.5, 21.5, 7, 21.5, 9);

  // Right side
  ctx.bezierCurveTo(23, 9.5, 24, 11, 24, 13);

  // Right bottom curve
  ctx.bezierCurveTo(24, 15.5, 22.5, 17.5, 20, 18.5);

  // Bottom right
  ctx.lineTo(8, 19.5);

  // Bottom curve back to start
  ctx.bezierCurveTo(7, 19.8, 6, 20, 6, 20);

  ctx.closePath();
  ctx.fill();

  ctx.restore();

  return canvas.toDataURL('image/png', 1.0);
};
